/*
  ves-pack-card-rows.js

  Per-row equalizer for .ves-pack-card. Two jobs:

  1. Name height: for every row of cards on screen, find the tallest
     natural name height (1 line vs 2 lines) and set every name in that
     row to that height via inline min-height. A row of all-short-names
     stays single-line; a row with even one long name gets a 2-line
     reserve on every card so the tagline / price / CTA below all line
     up. No global min-height in the CSS; this is the only source.

  2. Tagline height + visibility: same per-row treatment for taglines
     when the row has any tagline content. If NO card in a row has a
     tagline at all, every tagline element in that row is set to
     `display: none` so the body's flex gap collapses and the cards in
     that row tighten up visually.

  The equalizer is idempotent: every run resets the inline styles first,
  re-measures, and re-applies. It runs on DOMContentLoaded, on window
  resize (debounced via requestAnimationFrame), and whenever the DOM
  adds or removes .ves-pack-card nodes (filter/sort, async loads, etc.).
*/

(function () {
  'use strict';

  var SELECTOR_CARD = '.ves-pack-card';
  var SELECTOR_NAME = '.ves-pack-card__name';
  var SELECTOR_TAGLINE = '.ves-pack-card__tagline';
  var SELECTOR_PRICES = '.ves-pack-card__prices';
  var SELECTOR_BUNDLE_SLOT = '.ves-pack-card__bundle-slot';
  var SELECTOR_BUNDLE_LINK = '.ves-pack-card__bundle';
  var ROW_TOLERANCE = 4;

  function equalize() {
    var cards = Array.prototype.slice.call(document.querySelectorAll(SELECTOR_CARD));
    // Search-modal cards manage their own fixed geometry (compact variant)
    // and float OVER the page, so grouping them into the page's rows would
    // cross-contaminate min-heights. Leave them alone.
    cards = cards.filter(function (card) {
      return !(card.closest && card.closest('#search-modal'));
    });
    if (!cards.length) return;

    cards.forEach(function (card) {
      var name = card.querySelector(SELECTOR_NAME);
      var tag = card.querySelector(SELECTOR_TAGLINE);
      var prices = card.querySelector(SELECTOR_PRICES);
      var slot = card.querySelector(SELECTOR_BUNDLE_SLOT);
      if (name) name.style.minHeight = '';
      if (tag) {
        tag.style.minHeight = '';
        tag.style.display = '';
      }
      if (prices) prices.style.minHeight = '';
      if (slot) {
        slot.style.display = '';
        slot.style.minHeight = '';
      }
    });

    var rows = [];
    cards.forEach(function (card) {
      if (!card.offsetParent && card.getClientRects().length === 0) return;
      var top = card.getBoundingClientRect().top + window.scrollY;
      var row = null;
      for (var i = 0; i < rows.length; i++) {
        if (Math.abs(rows[i].top - top) <= ROW_TOLERANCE) {
          row = rows[i];
          break;
        }
      }
      if (!row) {
        row = { top: top, cards: [] };
        rows.push(row);
      }
      row.cards.push(card);
    });

    rows.forEach(function (row) {
      var maxNameH = 0;
      var maxTagH = 0;
      var maxPricesH = 0;
      var maxSlotH = 0;
      var rowHasTagline = false;
      var rowHasBundleLink = false;

      row.cards.forEach(function (card) {
        var name = card.querySelector(SELECTOR_NAME);
        var tag = card.querySelector(SELECTOR_TAGLINE);
        var prices = card.querySelector(SELECTOR_PRICES);
        var slot = card.querySelector(SELECTOR_BUNDLE_SLOT);
        if (name) {
          var nh = name.getBoundingClientRect().height;
          if (nh > maxNameH) maxNameH = nh;
        }
        if (tag) {
          var text = (tag.textContent || '').trim();
          if (text) {
            rowHasTagline = true;
            var th = tag.getBoundingClientRect().height;
            if (th > maxTagH) maxTagH = th;
          }
        }
        if (prices) {
          var ph = prices.getBoundingClientRect().height;
          if (ph > maxPricesH) maxPricesH = ph;
        }
        if (card.querySelector(SELECTOR_BUNDLE_LINK)) {
          rowHasBundleLink = true;
        }
        if (slot) {
          var sh = slot.getBoundingClientRect().height;
          if (sh > maxSlotH) maxSlotH = sh;
        }
      });

      row.cards.forEach(function (card) {
        var name = card.querySelector(SELECTOR_NAME);
        var tag = card.querySelector(SELECTOR_TAGLINE);
        var prices = card.querySelector(SELECTOR_PRICES);
        var slot = card.querySelector(SELECTOR_BUNDLE_SLOT);
        if (name && maxNameH > 0) {
          name.style.minHeight = Math.ceil(maxNameH) + 'px';
        }
        if (tag) {
          if (rowHasTagline) {
            tag.style.minHeight = Math.ceil(maxTagH) + 'px';
          } else {
            tag.style.display = 'none';
          }
        }
        if (prices && maxPricesH > 0) {
          prices.style.minHeight = Math.ceil(maxPricesH) + 'px';
        }
        if (slot) {
          if (!rowHasBundleLink) {
            slot.style.display = 'none';
          } else if (maxSlotH > 0) {
            slot.style.minHeight = Math.ceil(maxSlotH) + 'px';
          }
        }
      });
    });
  }

  var scheduled = false;
  function scheduleEqualize() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      equalize();
    });
  }

  function init() {
    equalize();
    window.addEventListener('resize', scheduleEqualize, { passive: true });

    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(scheduleEqualize);
    }

    document.querySelectorAll(SELECTOR_CARD + ' img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', scheduleEqualize, { once: true });
    });

    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          /* A card's visibility toggled (the search filter sets/removes
             data-hidden). That's an attribute mutation, not childList, so
             without handling it here the per-row min-heights stay stale and
             the layout only corrects on the next resize. Re-equalize so the
             now-visible cards regroup into their actual rows. */
          if (m.type === 'attributes') {
            var t = m.target;
            if (t && t.nodeType === 1 && t.matches && t.matches(SELECTOR_CARD)) {
              scheduleEqualize();
              return;
            }
            continue;
          }
          if (m.type !== 'childList') continue;
          if (m.addedNodes.length === 0 && m.removedNodes.length === 0) continue;
          var nodes = [].concat(
            Array.prototype.slice.call(m.addedNodes),
            Array.prototype.slice.call(m.removedNodes)
          );
          for (var j = 0; j < nodes.length; j++) {
            var node = nodes[j];
            if (node.nodeType !== 1) continue;
            if (
              (node.matches && node.matches(SELECTOR_CARD)) ||
              (node.querySelector && node.querySelector(SELECTOR_CARD))
            ) {
              scheduleEqualize();
              return;
            }
          }
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-hidden']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
